'use client'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  InputLabel,
  styled,
} from '@mui/material'
import { useContext, useMemo } from 'react'
import SelectInput from '@components/SelectInput'
import { useTranslation } from 'react-i18next'
import { Text } from '@components/Helpers'
import SelectIcon from '@assets/SelectIcon'
import { CheckCircle2, Sparkles, SlidersHorizontal, X } from 'lucide-react'
import { ExplorerContext } from '@context/Explorer/context'
import OverviewCheckbox from '@components/OverviewCheckbox'
import { ExplorerTraitSummary } from 'types/explorer'
import { DewiBreakpointsLg, DewiBreakpointsMd } from '@furry/tokens'
import { useSearchParams } from 'next/navigation'

const Label = styled(InputLabel)({
  marginBottom: '0.6rem',
  color: 'var(--dewi_colors_gray_0)',
  fontFamily: 'var(--font-nunito), sans-serif',
  lineHeight: 1.3,
  fontWeight: '600',
  fontSize: '1rem',
})

const FilterForm = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'hidden',
}))

const ModalDrawer = styled(Drawer)(({ theme }) => ({
  '	.MuiDrawer-paper': {
    backgroundImage: 'unset',
    overflowY: 'hidden',
    backgroundColor: 'var(--dewi_surface_page)',
    borderLeft: 'none',
    margin: 0,
    borderRadius: 0,
    height: '100vh',
    [theme.breakpoints.down(parseInt(DewiBreakpointsMd))]: {
      width: '100%',
    },
    [theme.breakpoints.between(
      parseInt(DewiBreakpointsMd),
      parseInt(DewiBreakpointsLg),
    )]: {
      width: 350,
    },
    [theme.breakpoints.up(parseInt(DewiBreakpointsLg))]: {
      width: 400,
    },
  },
  backdropFilter: 'blur(5px)',
}))

const ModalAccordion = styled(Accordion)(({ theme }) => ({
  '	.MuiAccordion-paper': {
    display: 'none',
  },
  background: 'transparent',
  boxShadow: 'none',
  width: '100%',
  [theme.breakpoints.up(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
}))

const AttributesAccordion = styled(Accordion)(() => ({
  background: 'transparent',
  boxShadow: 'none',
  width: '100%',
  '&:before': {
    display: 'none',
  },
}))

const AccordionSummaryText = { fontWeight: 'bold', fontSize: '1rem' }

const ExplorerModal = ({ traits }: { traits: ExplorerTraitSummary[] }) => {
  const { t } = useTranslation()
  const {
    addFilterOption,
    handleToggleShowFilter,
    showFilter,
    clearFilterOptions,
  } = useContext(ExplorerContext)
  const params = useSearchParams()

  const cancelFilterHandler = () => {
    clearFilterOptions()
    handleToggleShowFilter()
  }

  // Trait types and their values come straight from the static manifest, via
  // the server layout, so the filter list needs no network round trip.
  const selectInputComponent = useMemo(
    () =>
      traits.map(({ traitType, values }) => (
        <Box key={traitType} width="100%" marginBottom={3}>
          <Label>{traitType}</Label>
          <SelectInput
            sx={{
              menuItem: {},
              input: {
                backgroundColor: 'var(--dewi_colors_dark_7)',
                borderRadius: 'var(--dewi_radius_rounded)',
              },
            }}
            onChange={(value) => addFilterOption(traitType as never, value)}
            items={values.map((trait) => ({
              name: `${trait.value} (${trait.count})`,
              value: trait.value,
            }))}
            initialValue={params.get(traitType) || ''}
          />
        </Box>
      )),
    [params, traits],
  )

  return (
    <ModalDrawer
      open={showFilter}
      anchor="right"
      onClose={handleToggleShowFilter}
    >
      <FilterForm role="presentation">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding="1rem"
          paddingBottom={'0.5rem'}
        >
          <Box display="flex" alignItems="center">
            <SlidersHorizontal size={20} strokeWidth={1.75} className="mr-1.5" />
            <Text sx={{ fontWeight: 'bold' }}>
              {t('explorerPage.overview.form.filters')}
            </Text>
          </Box>
          <button
            className="btn btn-icon text-gray-2"
            onClick={handleToggleShowFilter}
          >
            <X size={20} strokeWidth={1.75} />
          </button>
        </Box>
        <div className="w-100 px-1 overflow-y-scroll h-[100%]">
          <ModalAccordion defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<SelectIcon />} sx={{ height: 70 }}>
              <div className="flex flex-col w-full">
                <Divider component="div" />
                <div className="flex items-center">
                  <div className="mr-2.5">
                    <CheckCircle2 size={22} strokeWidth={1.75} />
                  </div>
                  <Text sx={AccordionSummaryText}>
                    {t('explorerPage.overview.form.status')}
                  </Text>
                </div>
                <Divider component="div" />
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div className="flex flex-col gap-3 w-full ml-4">
                <OverviewCheckbox inModal />
              </div>
            </AccordionDetails>
          </ModalAccordion>
          <AttributesAccordion defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<SelectIcon />} sx={{ height: 70 }}>
              <div className="flex flex-col w-full">
                <Divider component="div" />
                <div className="flex items-center">
                  <div className="mr-2.5">
                    <Sparkles size={22} strokeWidth={1.75} />
                  </div>
                  <Text sx={AccordionSummaryText}>
                    {t('explorerPage.overview.form.attributes')}
                  </Text>
                </div>
                <Divider component="div" />
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div className="mt-2">{selectInputComponent}</div>
            </AccordionDetails>
          </AttributesAccordion>
        </div>
        <div className="flex justify-center py-4">
          <button
            className="btn font-bold capitalize w-28"
            onClick={cancelFilterHandler}
          >
            {t('explorerPage.modal.clearButton')}
          </button>
        </div>
      </FilterForm>
    </ModalDrawer>
  )
}

export default ExplorerModal
